import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  adamDefaultsPageQuery,
  categorySubcategoriesQuery,
  populateAdamDefaults,
  subcategoryOperationsQuery,
  type AdamDefaultsForm,
} from '../../api/settingsEaseedeskAdamIntopDefaults'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Easeedesk -> Adam Intop -> Populate Default Analyser Actions
 * Details, element cu element fata de `easeedesk_settings/adam_intop/
 * populate_default_sp_actions_details.php`: avertismentul cu cate randuri
 * exista deja, categoria -> subcategoria -> operatiunea (aduse in cascada,
 * ca in `-1.js`), tipurile si firmele cu bife, butonul Populate cu
 * intrebarea de swal si aceleasi mesaje.
 */
const EMPTY: AdamDefaultsForm = { category: '', subcategory: '', operation: '', run_only_on_ticket_types: [], run_only_on_companies: [] }

export function AdamIntopDefaultActionsPage() {
  const client = useQueryClient()
  const page = useQuery(adamDefaultsPageQuery())
  const [form, setForm] = useState<AdamDefaultsForm>(EMPTY)
  const subcategories = useQuery(categorySubcategoriesQuery(Number(form.category)))
  const operations = useQuery(subcategoryOperationsQuery(Number(form.subcategory)))
  const [busy, setBusy] = useState(false)

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!window.confirm('Are you sure you want to change these settings?')) return
    setBusy(true)
    void populateAdamDefaults(form)
      .then((answer) => {
        setBusy(false)
        if (answer.status === 1) {
          toast('Successfully saved.', 'Success')
          void client.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'adam_intop', 'default_actions'] })
        } else {
          toast(answer.message, 'Error', 'error')
        }
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      })
  }

  const count = page.data.count_default_active_actions_for_this_sp
  const label = { fontSize: 12 }

  return (
    <div>
      <h4>
        <strong>Adam Intop</strong> Populate Default Analyser Actions Details [for <strong> {page.data.providerName} </strong> SP]
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Populate <strong>specific details</strong> for Adam Intop default actions
      </label>
      <hr />

      <form id="populate-default-actions-form" onSubmit={submit}>
        {count > 0 && (
          <div className="alert alert-warning" role="alert">
            <strong>Warning!</strong> There are already assigned <strong>{count}</strong> numbers of specific entries asociated with
            this Sevice Provider. This actions data will be replaced!
          </div>
        )}

        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label className="label bg-default color-white" style={label}>
                Match Ticket Category
              </label>
              <SelectPicker
                id="populate-category"
                name="category"
                className="form-control"
                title="Select..."
                options={page.data.categories.map((c) => ({ value: String(c.ID), label: c.category_name }))}
                value={form.category}
                onChange={(v) => setForm({ ...form, category: v, subcategory: '', operation: '' })}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="label bg-default color-white" style={label}>
                Match Ticket Subcategory
              </label>
              <select
                name="subcategory"
                className="form-control "
                value={form.subcategory}
                onChange={(e) => setForm({ ...form, subcategory: e.target.value, operation: '' })}
              >
                <option value="">Select...</option>
                {(subcategories.data?.subcategories ?? []).map((s) => (
                  <option key={s.ID} value={String(s.ID)}>
                    {s.subcategory_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="label bg-default color-white" style={label}>
                Match Ticket Operation
              </label>
              <SelectPicker
                id="populate-operation"
                name="operation"
                className="form-control"
                title="Select..."
                options={(operations.data?.operations ?? []).map((o) => ({ value: String(o.ID), label: o.operation_name }))}
                value={form.operation}
                onChange={(v) => setForm({ ...form, operation: v })}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label className="label bg-default color-white" style={label}>
                Run only on this ticket types{' '}
              </label>
              <SelectPicker
                id="run_only_on_ticket_types"
                name="run_only_on_ticket_types[]"
                className="form-control"
                title="Nothing selected"
                multiple
                options={page.data.all_ticket_types.map((t) => ({ value: String(t.ID), label: `${t.short_name} (${t.type_name})` }))}
                value={form.run_only_on_ticket_types}
                onChange={(v) => setForm({ ...form, run_only_on_ticket_types: v })}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="label bg-default color-white" style={label}>
                Run only on these companies{' '}
              </label>
              <SelectPicker
                id="run_only_on_companies"
                name="run_only_on_companies[]"
                className="form-control"
                title="Nothing selected"
                multiple
                options={page.data.allCompaniesSp.map((c) => ({ value: String(c.ID), label: c.name }))}
                value={form.run_only_on_companies}
                onChange={(v) => setForm({ ...form, run_only_on_companies: v })}
              />
            </div>
          </div>
        </div>

        <hr className="dotted" />

        <div className="col-md-12 center-block">
          <button id="saveaisettingspopulateactions" type="submit" className="btn btn-success center-block" disabled={busy}>
            Populate
          </button>
        </div>
      </form>
    </div>
  )
}
